import { useMemo, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { useFormErrorMessageMap } from 'utils/react-hook-form/reactHookForm.hooks'
import {
  isTokenExpiredError,
  isNotFoundError,
  isSecurityError,
  isInvalidCredentialsError,
} from 'api/utils/apollo/errors/isError.hooks'
import { type DashMessages } from 'intl/intl.types'
import { useEnterpriseAccountAccountSetup } from 'domains/public/components/complete-account-setup/utils/enterpriseAccountAccountSetup.hooks'
import { useFormReCaptchaCheckbox } from 'gin-and-tonic/containers/re-captcha-check-box/useFormReCaptchaCheckBox.hooks'

interface Props {
  token: string
  initialData?: AccountSetupFormData
}

type AccountSetupFormData = {
  password: string
  confirmPassword: string
  givenName: string
  surname: string
  reCaptchaToken: string
}

const defaultValues: AccountSetupFormData = {
  password: '',
  confirmPassword: '',
  givenName: '',
  surname: '',
  reCaptchaToken: '',
}

export const usePublicAccountSetupDetails = ({ token, initialData = defaultValues }: Props) => {
  const { triggerAccountSetup, apiLoading, apiSuccess, apiError } =
    useEnterpriseAccountAccountSetup()

  const { register, handleSubmit, errors, watch, setValue } = useForm<AccountSetupFormData>({
    defaultValues: initialData,
  })

  const onSubmit = handleSubmit(({ password, givenName, surname, reCaptchaToken }) => {
    triggerAccountSetup({
      rawToken: token,
      password,
      givenName: givenName.trim(),
      surname: surname.trim(),
      reCaptchaToken,
    })
  })

  const passwordFieldRef = register({ required: true, minLength: 8 })
  const confirmPasswordFieldRef = register({
    required: true,
    validate: confirmPasswordValue => watch('password') === confirmPasswordValue,
  })
  const givenNameFieldRef = register({ required: true })
  const surnameFieldRef = register({ required: false })

  const { reCaptchaElement, resetReCaptcha } = useFormReCaptchaCheckbox({
    setValue,
    register,
    action: 'public-complete-account-setup-re-captcha',
    error: errors.reCaptchaToken,
  })

  useEffect(() => {
    if (apiError) resetReCaptcha()
  }, [apiError, resetReCaptcha])

  const passwordErrorMessageId = useFormErrorMessageMap({
    required: 'public.createAccount.form.fields.password.error.required',
    minLength: 'public.createAccount.form.fields.password.error.minLength',
  })(errors.password)

  const confirmPasswordErrorMessageId = useFormErrorMessageMap({
    validate: 'public.createAccount.form.fields.confirmPassword.error.validate',
  })(errors.confirmPassword)

  const givenNameErrorMessageId = useFormErrorMessageMap({
    required: 'public.createAccount.form.fields.givenName.error.required',
  })(errors.givenName)

  const surnameErrorMessageId = useFormErrorMessageMap({
    required: 'public.createAccount.form.fields.surname.error.required',
  })(errors.surname)

  const tokenErrorMessageId: DashMessages | undefined = useMemo(() => {
    if (!apiError) return undefined

    if (isTokenExpiredError(apiError)) {
      return 'public.accountSetup.content.error.expiredToken'
    }

    if (isSecurityError(apiError)) {
      return 'public.accountSetup.content.error.insecurePassword'
    }

    if (isNotFoundError(apiError)) {
      return 'public.accountSetup.content.error.notFound'
    }

    return 'public.accountSetup.content.error.common'
  }, [apiError])

  const formErrorMessageId: DashMessages | undefined = useMemo(() => {
    if (!apiError) return undefined

    if (isSecurityError(apiError)) {
      return 'public.createAccount.form.error.insecurePassword'
    }

    if (isInvalidCredentialsError(apiError)) {
      return 'public.createAccount.form.error.existingEmail'
    }

    // Default to no formErrorMessageId
    // tokenErrorMessageId should be set instead
    return undefined
  }, [apiError])

  return {
    onSubmit,
    passwordFieldRef,
    confirmPasswordFieldRef,
    givenNameFieldRef,
    surnameFieldRef,
    passwordErrorMessageId,
    confirmPasswordErrorMessageId,
    givenNameErrorMessageId,
    surnameErrorMessageId,
    disableFormFields: apiLoading || apiSuccess,
    accountSetupSuccess: apiSuccess,
    tokenErrorMessageId,
    formErrorMessageId,
    _setValue: setValue,
    reCaptchaElement,
  }
}
